import { Component, computed, input } from '@angular/core';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Stat Card Trend
 */
export type StatTrend = 'up' | 'down' | 'neutral';

/**
 * Stat Card Component
 *
 * Display key metrics and KPIs with trend indicators.
 */
@Component({
  selector: 'mg3p-stat-card',
  standalone: true,
  imports: [IconComponent],
  template: `
    <div
      class="stat-card"
      [class.stat-card--clickable]="clickable()"
      [class.stat-card--loading]="loading()"
    >
      <div class="stat-card__header">
        @if (iconName()) {
          <span class="stat-card__icon" aria-hidden="true">
            <mg3p-icon [name]="iconName()!" size="lg" />
          </span>
        }
        <span class="stat-card__label">{{ label() }}</span>
      </div>

      <div class="stat-card__body">
        @if (loading()) {
          <div class="stat-card__skeleton"></div>
        } @else {
          <div class="stat-card__value">{{ value() }}</div>

          @if (trend() && trendValue()) {
            <div
              class="stat-card__trend"
              [class.stat-card__trend--up]="trend() === 'up'"
              [class.stat-card__trend--down]="trend() === 'down'"
              [class.stat-card__trend--neutral]="trend() === 'neutral'"
            >
              <span class="stat-card__trend-icon" aria-hidden="true">
                <mg3p-icon [name]="trendIconName()" size="sm" />
              </span>
              <span class="stat-card__trend-value">{{ trendValue() }}</span>
              @if (trendLabel()) {
                <span class="stat-card__trend-label">{{ trendLabel() }}</span>
              }
            </div>
          }
        }
      </div>

      @if (description()) {
        <div class="stat-card__footer">
          {{ description() }}
        </div>
      }
    </div>
  `,
  styles: [`
    .stat-card {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
      padding: var(--spacing-lg);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      transition: all var(--transition-fast);
    }

    .stat-card--clickable {
      cursor: pointer;
    }

    .stat-card--clickable:hover {
      border-color: var(--color-primary);
      box-shadow: var(--shadow-md);
      transform: translateY(-2px);
    }

    .stat-card__header {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
    }

    .stat-card__icon {
      display: flex;
      align-items: center;
      color: var(--color-primary);
      line-height: 1;
    }

    .stat-card__label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .stat-card__body {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);
    }

    .stat-card__value {
      font-size: var(--font-size-3xl);
      font-weight: var(--font-weight-bold);
      color: var(--color-text-primary);
      line-height: 1.2;
    }

    .stat-card__trend {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
    }

    .stat-card__trend--up {
      color: var(--color-success);
    }

    .stat-card__trend--down {
      color: var(--color-danger);
    }

    .stat-card__trend--neutral {
      color: var(--color-text-secondary);
    }

    .stat-card__trend-icon {
      display: flex;
      align-items: center;
    }

    .stat-card__trend-label {
      color: var(--color-text-tertiary);
    }

    .stat-card__footer {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      padding-top: var(--spacing-sm);
      border-top: 1px solid var(--color-border);
    }

    .stat-card__skeleton {
      height: 48px;
      background: linear-gradient(
        90deg,
        var(--color-surface-secondary) 25%,
        var(--color-surface-hover) 50%,
        var(--color-surface-secondary) 75%
      );
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: var(--radius-md);
    }

    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .stat-card,
      .stat-card__skeleton {
        animation: none;
        transition: none;
      }

      .stat-card--clickable:hover {
        transform: none;
      }
    }
  `],
})
export class StatCardComponent {
  label = input.required<string>();
  value = input.required<string>();
  iconName = input<NavIconName | undefined>(undefined);
  trend = input<StatTrend | null>(null);
  trendValue = input<string>('');
  trendLabel = input<string>('');
  description = input<string>('');
  loading = input<boolean>(false);
  clickable = input<boolean>(false);

  readonly trendIconName = computed((): NavIconName => {
    switch (this.trend()) {
      case 'up':
        return 'trend-up';
      case 'down':
        return 'trend-down';
      case 'neutral':
        return 'trend-neutral';
      default:
        return 'trend-neutral';
    }
  });
}
